import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiService } from '../services/api.service';
import { ShellComponent } from '../ui/shell.component';

interface HoleLine {
  userId: number | null;
  teamId: number | null;
  displayName: string;
  gross: number | null;
  strokes: number;
  net: number | null;
  kept: boolean | null;
}

interface HoleView {
  sequence: number;
  par: number;
  displayHoleNumber: number | null;
  teeName: string;
  teeColor: string | null;
  lines: HoleLine[];
  countingScore: number | null;
  complete: boolean;
}

interface ScoreGroup {
  groupId: number;
  label: string;
}

interface Scorecard {
  eventId: number;
  eventName: string;
  format: string;
  formatLabel: string;
  teamGross: boolean;
  runningTee: boolean;
  oceans6: boolean;
  organizer: boolean;
  roundNumber: number;
  competitorId: number | null;
  competitorName: string | null;
  holes: HoleView[];
  teams: Array<{ teamId: number; name: string }>;
  groups: ScoreGroup[];
  canPickGroup: boolean;
  groupId: number | null;
  groupLabel: string | null;
  notice: string | null;
}

@Component({
  selector: 'app-score',
  standalone: true,
  imports: [FormsModule, RouterLink, ShellComponent],
  templateUrl: './score.component.html',
})
export class ScoreComponent implements OnInit {
  roundId = 0;
  teamId: number | null = null;
  groupId: number | null = null;
  card: Scorecard | null = null;
  holeIndex = 0;
  error = '';
  saving = false;

  constructor(private route: ActivatedRoute, private api: ApiService) {}

  async ngOnInit(): Promise<void> {
    this.roundId = Number(this.route.snapshot.paramMap.get('roundId'));
    const requestedGroup = Number(this.route.snapshot.queryParamMap.get('group'));
    if (Number.isInteger(requestedGroup) && requestedGroup > 0) this.groupId = requestedGroup;
    const requestedHole = Number(this.route.snapshot.queryParamMap.get('hole'));
    await this.load();
    if (this.card && Number.isInteger(requestedHole) && requestedHole >= 0 && requestedHole < this.card.holes.length) {
      this.holeIndex = requestedHole;
    }
    const undecided = this.firstUndecidedIndex();
    if (undecided != null && this.holeIndex > undecided) this.holeIndex = undecided;
  }

  get hole(): HoleView | null {
    return this.card?.holes[this.holeIndex] ?? null;
  }

  get backTo(): unknown[] {
    return this.card ? ['/events', this.card.eventId] : ['/leagues'];
  }

  get title(): string {
    const hole = this.hole;
    return hole ? `Hole ${hole.displayHoleNumber || hole.sequence}` : 'Scores';
  }

  async load(): Promise<void> {
    this.error = '';
    try {
      this.applyCard(await this.api.post<Scorecard>('scorecard', {
        roundId: this.roundId,
        teamId: this.teamId,
        groupId: this.groupId,
      }));
      if (this.card && this.holeIndex >= this.card.holes.length) this.holeIndex = 0;
    } catch (error: unknown) {
      this.error = error instanceof Error ? error.message : 'Could not load the card';
    }
  }

  async changeTeam(teamId: number): Promise<void> {
    this.teamId = Number(teamId);
    await this.load();
  }

  async changeGroup(groupId: number): Promise<void> {
    this.groupId = Number(groupId);
    await this.load();
  }

  previous(): void {
    if (this.holeIndex > 0) this.holeIndex -= 1;
  }

  get needsDecision(): boolean {
    return this.holeNeedsDecision(this.hole);
  }

  private firstUndecidedIndex(): number | null {
    if (!this.card?.oceans6) return null;
    const index = this.card.holes.findIndex(hole => this.holeNeedsDecision(hole));
    return index >= 0 ? index : null;
  }

  private holeNeedsDecision(hole: HoleView | null): boolean {
    if (!this.card?.oceans6 || !hole) return false;
    return hole.lines.some(line => line.gross != null && line.kept == null);
  }

  next(): void {
    if (this.needsDecision || !this.card || this.holeIndex >= this.card.holes.length - 1) return;
    this.holeIndex += 1;
  }

  async bump(line: HoleLine, delta: number): Promise<void> {
    const par = this.hole?.par ?? 4;
    const next = line.gross == null ? par : line.gross + delta;
    if (next < 1 || next > 20) return;
    line.gross = next;
    await this.save();
  }

  get hasScores(): boolean {
    return !!this.hole?.lines.some(line => line.gross != null);
  }

  get clearLabel(): string {
    return (this.hole?.lines.length ?? 0) === 2 ? 'Clear both scores' : 'Clear scores';
  }

  decisionLocked(line: HoleLine): boolean {
    return !!this.card?.oceans6 && !this.card.organizer && line.kept != null;
  }

  get clearLocked(): boolean {
    return !!this.hole?.lines.some(line => this.decisionLocked(line));
  }

  async clearScores(): Promise<void> {
    if (!this.hole || !this.hasScores || this.clearLocked) return;
    for (const line of this.hole.lines) {
      line.gross = null;
      line.net = null;
      line.kept = null;
    }
    await this.save(true);
  }

  async setKept(line: HoleLine, kept: boolean): Promise<void> {
    if (this.decisionLocked(line)) return;
    if (line.gross == null) {
      this.error = 'Enter the gross score first.';
      return;
    }
    line.kept = kept;
    await this.save();
  }

  keptCount(par: number, userId: number | null): number {
    if (!this.card) return 0;
    return this.card.holes.filter(hole => hole.par === par && hole.lines.some(line => line.userId === userId && line.kept)).length;
  }

  canKeep(par: number, userId: number | null): boolean {
    const quota = this.quota(par);
    return quota > 0 && this.keptCount(par, userId) < quota;
  }

  canDiscard(par: number, sequence: number, userId: number | null): boolean {
    const quota = this.quota(par);
    if (!quota || !this.card || userId == null) return false;
    const stillAvailable = this.card.holes.filter(hole => {
      if (hole.par !== par || hole.sequence === sequence) return false;
      const line = hole.lines.find(item => item.userId === userId);
      return line?.kept !== false;
    }).length;
    return stillAvailable >= quota;
  }

  mustKeep(hole: HoleView, line: HoleLine): boolean {
    if (line.userId == null || line.kept === true) return false;
    return !this.canDiscard(hole.par, hole.sequence, line.userId);
  }

  private applyCard(card: Scorecard): void {
    this.card = {
      ...card,
      groups: card.groups ?? [],
      organizer: !!card.organizer,
      canPickGroup: !!card.canPickGroup,
      groupId: card.groupId ?? null,
      groupLabel: card.groupLabel ?? null,
      notice: card.notice ?? null,
    };
    this.groupId = this.card.groupId;
    if (this.card.groupId == null) this.teamId = this.card.competitorId;
  }

  private quota(par: number): number {
    if (par === 3 || par === 5) return 1;
    if (par === 4) return 4;
    return 0;
  }

  private async save(clearing = false): Promise<void> {
    if (!this.hole || this.saving) return;
    this.saving = true;
    this.error = '';
    const lines = clearing ? this.hole.lines : this.hole.lines.filter(line => line.gross != null);
    try {
      const saved = await this.api.put<Scorecard>('score', {
        roundId: this.roundId,
        teamId: this.teamId,
        groupId: this.groupId,
        scores: lines.map(line => ({
          sequence: this.hole?.sequence,
          userId: line.userId,
          teamId: line.teamId,
          gross: line.gross,
          kept: line.kept,
        })),
      });
      const index = this.holeIndex;
      this.applyCard(saved);
      this.holeIndex = index;
    } catch (error: unknown) {
      this.error = error instanceof Error ? error.message : 'Could not save the score';
      await this.load();
    } finally {
      this.saving = false;
    }
  }
}
